import React from 'react';
import { User, MoreHorizontal, Mail, Phone, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type EmployeeStatus = 'active' | 'onboarding' | 'offboarding' | 'inactive';

interface HumanEmployeeProps {
  employee: {
    id: string;
    name: string;
    role: string;
    avatar: string;
    department: string;
    status: EmployeeStatus;
  };
}

const HumanEmployeeCard: React.FC<HumanEmployeeProps> = ({ employee }) => {
  const statusColors = {
    active: "bg-green-100 text-green-800",
    onboarding: "bg-blue-100 text-blue-800",
    offboarding: "bg-amber-100 text-amber-800",
    inactive: "bg-gray-100 text-gray-800"
  };
  
  const statusLabels = {
    active: "Active",
    onboarding: "Onboarding",
    offboarding: "Offboarding",
    inactive: "Inactive"
  };
  
  return (
    <Card className="overflow-hidden transition-all duration-300 hover:shadow-md h-full flex flex-col">
      <CardHeader className="pb-2 relative">
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            {employee.avatar ? (
              <img 
                src={employee.avatar} 
                alt={employee.name} 
                className="h-12 w-12 rounded-full object-cover" 
              />
            ) : (
              <User className="h-6 w-6 text-primary" />
            )}
          </div>
          <div className="space-y-1">
            <h3 className="font-medium text-base">{employee.name}</h3>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <p className="text-sm text-muted-foreground">{employee.role}</p>
              <div className="hidden sm:block text-muted-foreground">•</div>
              <span className="text-xs">{employee.department}</span>
            </div>
          </div>
        </div>
        <div className={cn(
          "absolute top-4 right-4 text-xs px-2 py-1 rounded-full",
          statusColors[employee.status]
        )}>
          {statusLabels[employee.status]}
        </div>
      </CardHeader>
      <CardContent className="pb-2 flex-grow">
        <div className="space-y-2">
          <div className="flex items-center text-sm gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Contact</span>
          </div>
          <div className="flex items-center text-sm gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Schedule</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-0 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1">View Profile</Button>
        <Button variant="outline" size="sm" className="w-10 px-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default HumanEmployeeCard;
